/* -------------------------------------------------------------------------- */
/* Toronto / Eastern time helpers                                             */
/*                                                                            */
/* The app is operated from Toronto, so "today"/"now" must always reflect the */
/* Eastern calendar day — NOT the browser's local timezone and NOT UTC.       */
/* `America/Toronto` handles the EST/EDT daylight-saving switch automatically. */
/* Never use `new Date().toISOString()` for "today": it returns UTC and rolls */
/* over to the next day in the evening for Eastern users.                      */
/* -------------------------------------------------------------------------- */

/** IANA timezone for all "current date/time" computations in the app. */
export const TORONTO_TZ = "America/Toronto";

/**
 * Current Toronto (EST/EDT) date parts for a given instant.
 * @param {Date} [date=new Date()] - The instant to read (defaults to now)
 * @returns {{ year: number, month: number, day: number }} month is 1-12
 */
export function torontoDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TORONTO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/**
 * Today's date in Toronto as "YYYY-MM-DD".
 * Drop-in replacement for `new Date().toISOString().slice(0, 10)`.
 * @returns {string} YYYY-MM-DD
 */
export function torontoToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: TORONTO_TZ });
}

/**
 * Current month in Toronto as "YYYY-MM".
 * Drop-in replacement for `new Date().toISOString().slice(0, 7)`.
 * @returns {string} YYYY-MM
 */
export function torontoMonthKey() {
  return torontoToday().slice(0, 7);
}

/**
 * True when value is a real calendar month key "YYYY-MM" (month 01–12).
 * Rejects format-only matches like "2025-99".
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidYearMonth(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}$/.test(value)) return false;
  const month = Number(value.slice(5, 7));
  return month >= 1 && month <= 12;
}

/** Current year in Toronto. Replacement for `new Date().getFullYear()`. */
export function torontoYear() {
  return torontoDateParts().year;
}

/** Current 0-indexed month in Toronto. Replacement for `new Date().getMonth()`. */
export function torontoMonthIndex() {
  return torontoDateParts().month - 1;
}

/** Current 1-indexed month in Toronto (1-12). */
export function torontoMonthNumber() {
  return torontoDateParts().month;
}

/** Current day-of-month in Toronto. Replacement for `new Date().getDate()`. */
export function torontoDayOfMonth() {
  return torontoDateParts().day;
}

/**
 * Round a number to 2 decimal places to avoid floating point precision issues
 * @param {number|string} num - The number to round (can be string or number)
 * @returns {number} The number rounded to 2 decimal places
 */
export const roundTo2 = (num) => {
  const n = Number(num);
  return isNaN(n) ? 0 : Math.round(n * 100) / 100;
};

/**
 * Max selectable date for the forecasting date picker (today + 5 days).
 * @returns {string} YYYY-MM-DD
 */
export function getForecastMaxDate() {
  // No upper limit — forecasts can be entered for any future date.
  return null;
}

/**
 * Build a display label for a clinic: "ClinicName (ClinicCode)" or just "ClinicCode"
 */
export function clinicLabel(clinic) {
  if (!clinic) return "";
  const code = clinic.clinic || "";
  const name = (clinic.name || clinic.clinic_name || "").trim();
  return name ? `${name} (${code})` : code;
}

/**
 * Convert API value to form string representation
 * @param {number|string|null|undefined} value - The value to format
 * @returns {string} The formatted string value
 */
export const formatValueForForm = (value) => {
  if (value === null || value === undefined) return "";
  return Number(value) === 0 ? "0" : value.toString();
};

/**
 * Clean money input to allow only numbers and one decimal point
 * @param {string} raw - The raw input string
 * @returns {string} Cleaned money string with max 2 decimal places
 */
export const cleanMoneyInput = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return "";

  // Remove all non-digit and non-dot characters
  let cleaned = text.replace(/[^\d.]/g, "");

  // Keep only first decimal point
  const parts = cleaned.split(".");
  if (parts.length > 1) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  // Handle edge case of just a dot
  if (cleaned === ".") return "0.";

  // Limit to 2 decimal places
  const dotPos = cleaned.indexOf(".");
  if (dotPos >= 0) {
    const intPart = cleaned.slice(0, dotPos);
    const decPart = cleaned.slice(dotPos + 1, dotPos + 3);
    return intPart + "." + decPart;
  }

  return cleaned;
};

/**
 * Convert value to number, returning 0 for invalid values
 * @param {any} x - Value to convert
 * @returns {number} The numeric value or 0
 */
export const toNumber = (x) => {
  if (x === "" || x === null || x === undefined) return 0;
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Validate if string is a valid number (unlimited decimal places)
 * @param {string} x - The string to validate
 * @returns {boolean} True if valid number format
 */
export const isValidNum = (x) => {
  if (x === "") return true;

  // Match: optional digits, optional (dot + any digits), no other characters
  return /^\d*\.?\d*$/.test(String(x));
};

/**
 * Validate if string is a valid signed number (unlimited decimal places).
 * Allows an optional leading minus sign so negative values can be entered
 * (e.g. corrections / clawbacks for the other-profit tier inputs).
 * @param {string} x - The string to validate
 * @returns {boolean} True if valid signed number format
 */
export const isValidSignedNum = (x) => {
  if (x === "") return true;
  const s = String(x);
  if (s === "-" || s === "-.") return true;
  return /^-?\d*\.?\d*$/.test(s);
};

/**
 * Safe string for display: avoids "[object Object]", handles select value/label objects.
 * @param {any} v - Value to normalize
 * @returns {string} Non-null string (trimmed for primitives)
 */
export function safeText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") {
    if ("value" in v) return String(v.value ?? "").trim();
    if ("label" in v) return String(v.label ?? "").trim();
    return "";
  }
  return String(v).trim();
}

/**
 * Convert value to number or null for invalid/missing (use toNumber for default 0).
 * @param {any} x - Value to convert
 * @returns {number|null} The numeric value or null
 */
export function toNumberOrNull(x) {
  if (x === "" || x === null || x === undefined) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/**
 * Single source for number validation with optional min/max.
 * @param {string|number} raw - Raw input
 * @param {{ min?: number, max?: number }} opts - Optional range
 * @returns {{ ok: boolean, num: number|null }}
 */
export function validateNumberField(raw, { min = -Infinity, max = Infinity } = {}) {
  const s = safeText(raw).trim();
  if (s === "") return { ok: true, num: null };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, num: null };
  if (n < min || n > max) return { ok: false, num: n };
  return { ok: true, num: n };
}

/** Whether a (parsed) number is valid base pay (>= 0). null allowed. */
export function isValidPay(n) {
  if (n === null) return true;
  return Number.isFinite(n) && n >= 0;
}

/** Whether a (parsed) number is valid percentage (0–100). null allowed. */
export function isValidPct(n) {
  if (n === null) return true;
  return Number.isFinite(n) && n >= 0 && n <= 100;
}

/**
 * Normalize practitioner type for UI (handles string, array, or JSON-like from API).
 * @param {string|string[]|null|undefined} value
 * @returns {string} "DDS" | "HYG" | or uppercased first segment
 */
export function normalizeTypeForUi(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    const first = value[0];
    if (first === null || first === undefined) return "";
    return normalizeTypeForUi(first);
    // recursive: first may be string
  }
  const text = String(value).trim();
  if (text.length === 0) return "";
  const looksLikeArray = text[0] === "[" && text[text.length - 1] === "]";
  if (looksLikeArray) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return normalizeTypeForUi(parsed[0]);
      }
    } catch (_) {
      // fall through
    }
  }
  const firstPart = text
    .replace(/\\/g, "")
    .replace(/[[\]"']/g, "")
    .split(",")[0]
    .trim();
  // toUpperCase() handles all 16 type codes uniformly — individual if-branches
  // per type were redundant because the result was identical in every case.
  return firstPart.toUpperCase();
}

/**
 * Badge color for practitioner type in UI.
 * @param {string} typeUi - "DDS" | "HYG" | etc.
 * @returns {string} Badge color key
 */
export function pickBadgeColor(typeUi) {
  if (typeUi === "DDS") return "neutral";
  if (typeUi === "HYG") return "success";
  if (typeUi === "HYGR") return "success"; // Restorative hygienist shares the green success color with HYG
  if (typeUi === "SRG" || typeUi === "SPC") return "danger"; // "danger" (rose) — previously "error" which is not a valid Badge color key
  if (typeUi === "PL" || typeUi === "AM") return "warning"; // Leadership roles get amber warning color
  return "neutral"; // All other types default to neutral gray
}
